import { getBackendUrl } from '@/lib/backendUrl';
import { getApiError } from '@/lib/utils';
import React, { useState } from 'react';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, ShieldCheck, ShieldAlert, PencilLine } from 'lucide-react';
import { toast } from 'sonner';
import { MappingBadge } from './MappingBadge';

const API = getBackendUrl();

function authHeaders() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const FLAG_STYLES = {
  billed: { label: 'Billed — blocked', className: 'bg-red-100 text-red-800' },
  deferred: { label: 'Deferred', className: 'bg-slate-200 text-slate-700' },
  waived: { label: 'Waived', className: 'bg-slate-200 text-slate-700' },
  on_other_draft: { label: 'On another active draft', className: 'bg-amber-100 text-amber-800' },
  gap: { label: 'Not previously covered', className: 'bg-sky-100 text-sky-800' },
};

export default function CellarLedgerPanel({ jobId, draft, onChanged }) {
  const cellar = draft.cellar_ledger || {};
  const cellarLine = (draft.service_lines || []).find((s) => s.is_cellar_ledger);
  const [rateInput, setRateInput] = useState('');
  const [editingRate, setEditingRate] = useState(false);
  const [newMonth, setNewMonth] = useState('');
  const [pendingMonths, setPendingMonths] = useState(
    (cellar.selected_months && cellar.selected_months.length > 0) ? cellar.selected_months : (cellar.suggested_months || [])
  );
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [savingRate, setSavingRate] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const savedMonths = cellar.selected_months || [];
  const monthsDirty = JSON.stringify([...pendingMonths].sort()) !== JSON.stringify([...savedMonths].sort());

  const saveRate = async () => {
    const value = Number(rateInput);
    if (!(value > 0)) {
      toast.error('Enter a monthly rate greater than 0.');
      return;
    }
    setSavingRate(true);
    try {
      await axios.put(`${API}/api/sales-invoicing/sites/${draft.site_id}/cellar-ledger-rate`, { monthly_rate: value }, { headers: authHeaders() });
      toast.success('Rate saved — now verify it to unblock this line.');
      setEditingRate(false);
      await onChanged();
    } catch (err) {
      toast.error(getApiError(err, 'Could not save the rate.'));
    } finally {
      setSavingRate(false);
    }
  };

  const toggleVerify = async () => {
    setVerifying(true);
    try {
      await axios.post(`${API}/api/sales-invoicing/sites/${draft.site_id}/cellar-ledger-rate/verify`, { verified: !cellar.rate_verified }, { headers: authHeaders() });
      await onChanged();
    } catch (err) {
      toast.error(getApiError(err, 'Could not update verification.'));
    } finally {
      setVerifying(false);
    }
  };

  const addMonth = () => {
    if (!newMonth) return;
    if (pendingMonths.includes(newMonth)) {
      toast.error('That month is already selected.');
      return;
    }
    setSaveFailed(false);
    setPendingMonths([...pendingMonths, newMonth].sort());
    setNewMonth('');
  };

  const removeMonth = (m) => {
    setSaveFailed(false);
    setPendingMonths(pendingMonths.filter((x) => x !== m));
  };

  const saveMonths = async () => {
    setSaving(true);
    setSaveFailed(false);
    try {
      await axios.put(`${API}/api/sales-invoicing/jobs/${jobId}/draft`, { selected_cellar_ledger_months: pendingMonths }, { headers: authHeaders() });
      toast.success('Cellar Ledger months saved to this draft.');
      await onChanged();
    } catch (err) {
      setSaveFailed(true);
      toast.error(getApiError(err, 'Could not save these months — a Billed month may be blocking it.'));
    } finally {
      setSaving(false);
    }
  };

  const flagFor = (m) => {
    const f = (cellar.month_flags || []).find((x) => x.month === m);
    return f ? f.status : null;
  };

  const suggested = cellar.suggested_months || [];
  const suggestionDiffers = suggested.length > 0 && JSON.stringify([...suggested].sort()) !== JSON.stringify([...pendingMonths].sort());
  const applySuggestion = () => {
    setSaveFailed(false);
    setPendingMonths(suggested);
  };

  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-3 space-y-3" data-testid="cellar-ledger-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold text-violet-800 uppercase">Cellar Ledger — separately verified monthly rate</div>
        {cellarLine && <MappingBadge mapping={cellarLine.mapping} />}
      </div>

      {cellar.suggestion_requires_manual_start && (
        <div className="rounded-md border border-sky-200 bg-sky-50 text-sky-900 text-sm px-3 py-2" data-testid="cellar-ledger-suggestion-manual-start">
          No earlier completed job found at this site — AbbeyZone has no history to suggest a start month from. Choose the months yourself below.
        </div>
      )}
      {suggested.length > 0 && (
        <div className="rounded-md border border-sky-200 bg-sky-50 text-sky-900 text-sm px-3 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="cellar-ledger-suggestion-banner">
          <span>
            Suggested ({cellar.suggestion_basis === 'repeat_cycle' ? 'from the last AZ-exported month' : 'since the previous visit'}): {suggested.join(', ') || '— none —'}
            {cellar.suggestion_unverified_against_qbo && <span className="italic"> — unverified against QBO, check it there yourself.</span>}
          </span>
          {suggestionDiffers && (
            <Button size="sm" variant="outline" onClick={applySuggestion} data-testid="cellar-ledger-apply-suggestion-btn">Use suggested months</Button>
          )}
        </div>
      )}
      {(cellar.coverage_gap_months || []).length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-sm px-3 py-2" data-testid="cellar-ledger-gap-warning">
          Gap — {cellar.coverage_gap_months.join(', ')} {cellar.coverage_gap_months.length === 1 ? 'is' : 'are'} not yet saved, billed, deferred or waived at this site.
          Save {cellar.coverage_gap_months.length === 1 ? 'it' : 'them'} below, or mark {cellar.coverage_gap_months.length === 1 ? 'it' : 'them'} Deferred/Waived at this site — never leave it unaddressed.
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <div className="text-sm">
          {cellar.monthly_rate != null ? (
            <span data-testid="cellar-ledger-rate-display">£{Number(cellar.monthly_rate).toFixed(2)} / month</span>
          ) : (
            <span className="text-slate-500" data-testid="cellar-ledger-rate-display">No monthly rate set yet</span>
          )}
          {cellar.monthly_rate != null && (
            cellar.rate_verified ? (
              <Badge className="bg-emerald-600 ml-2" data-testid="cellar-ledger-rate-verified-badge"><ShieldCheck className="h-3 w-3 mr-1" />Verified</Badge>
            ) : (
              <Badge variant="secondary" className="bg-amber-100 text-amber-800 ml-2" data-testid="cellar-ledger-rate-unverified-badge"><ShieldAlert className="h-3 w-3 mr-1" />Not verified</Badge>
            )
          )}
        </div>
        <div className="flex gap-2">
          {!editingRate && (
            <Button size="sm" variant="outline" onClick={() => { setEditingRate(true); setRateInput(cellar.monthly_rate != null ? String(cellar.monthly_rate) : (cellar.suggested_monthly_rate != null ? String(cellar.suggested_monthly_rate) : '')); }} data-testid="cellar-ledger-edit-rate-btn">
              <PencilLine className="h-3.5 w-3.5 mr-1" />{cellar.monthly_rate != null ? 'Edit rate' : 'Set rate'}
            </Button>
          )}
          {cellar.monthly_rate != null && !editingRate && (
            <Button size="sm" variant={cellar.rate_verified ? 'outline' : 'default'} onClick={toggleVerify} disabled={verifying} data-testid="cellar-ledger-toggle-verify-btn">
              {cellar.rate_verified ? 'Unverify' : 'Verify rate'}
            </Button>
          )}
        </div>
      </div>

      {cellar.monthly_rate == null && !editingRate && cellar.suggested_monthly_rate != null && (
        <div className="rounded-md border border-sky-200 bg-sky-50 text-sky-900 text-sm px-3 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="cellar-ledger-rate-suggestion-banner">
          <span>Suggested from {cellar.suggested_monthly_rate_source}: £{Number(cellar.suggested_monthly_rate).toFixed(2)}/month — still needs your verification before it can be used.</span>
          <Button size="sm" variant="outline" onClick={() => { setEditingRate(true); setRateInput(String(cellar.suggested_monthly_rate)); }} data-testid="cellar-ledger-use-suggested-rate-btn">Use this rate</Button>
        </div>
      )}

      {editingRate && (
        <div className="space-y-1" data-testid="cellar-ledger-rate-edit-row">
          <div className="flex items-center gap-2">
            <Input type="number" min="0.01" step="0.01" placeholder="Monthly rate" value={rateInput} onChange={(e) => setRateInput(e.target.value)} className="w-[160px]" data-testid="cellar-ledger-rate-input" />
            <Button size="sm" onClick={saveRate} disabled={savingRate} data-testid="cellar-ledger-save-rate-btn">Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditingRate(false)} data-testid="cellar-ledger-cancel-rate-btn">Cancel</Button>
          </div>
          {cellar.monthly_rate == null && cellar.suggested_monthly_rate != null && Number(rateInput) === Number(cellar.suggested_monthly_rate) && (
            <p className="text-xs text-sky-700" data-testid="cellar-ledger-rate-source-note">Prefilled from {cellar.suggested_monthly_rate_source} — saving still leaves it unverified until you click Verify rate.</p>
          )}
        </div>
      )}
      <p className="text-xs text-slate-500">Never the job's own audit price — a common monthly contract rate for this site, confirmed here deliberately.</p>

      <div className="space-y-2">
        <div className="text-xs font-medium text-slate-500">Selected months (quantity × rate)</div>
        <div className="flex flex-wrap gap-2" data-testid="cellar-ledger-month-chips">
          {pendingMonths.length === 0 && <span className="text-sm text-slate-400">No months selected yet.</span>}
          {pendingMonths.map((m) => {
            const flag = flagFor(m);
            const style = flag && FLAG_STYLES[flag];
            const isUnsaved = !savedMonths.includes(m);
            const chipClass = isUnsaved
              ? (saveFailed ? 'bg-red-50 border-red-400 border-dashed' : 'bg-white border-dashed border-amber-400')
              : 'bg-white';
            return (
              <span key={m} className={`inline-flex items-center gap-1 text-sm border rounded-full pl-3 pr-1.5 py-0.5 ${chipClass}`} data-testid={`cellar-ledger-month-chip-${m}`}>
                {m}
                {isUnsaved && (
                  <Badge
                    variant="secondary"
                    className={`text-[10px] px-1.5 py-0 ${saveFailed ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}
                    data-testid={`cellar-ledger-month-unsaved-${m}`}
                  >
                    {saveFailed ? 'Save failed' : 'Unsaved'}
                  </Badge>
                )}
                {style && <Badge variant="secondary" className={`${style.className} text-[10px] px-1.5 py-0`} data-testid={`cellar-ledger-month-flag-${m}`}>{style.label}</Badge>}
                <button type="button" onClick={() => removeMonth(m)} data-testid={`cellar-ledger-remove-month-${m}`} className="text-slate-400 hover:text-red-600">
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input type="month" value={newMonth} onChange={(e) => setNewMonth(e.target.value)} className="w-[160px] min-w-0" data-testid="cellar-ledger-add-month-input" />
          <Button size="sm" variant="outline" onClick={addMonth} data-testid="cellar-ledger-add-month-btn">Add month</Button>
          {monthsDirty && (
            <Button size="sm" onClick={saveMonths} disabled={saving} data-testid="cellar-ledger-save-months-btn">Save months</Button>
          )}
        </div>
      </div>

      <div className="flex justify-between text-sm pt-1 border-t border-violet-200">
        <span className="text-slate-500">{cellar.quantity || 0} month(s) × rate</span>
        <span data-testid="cellar-ledger-line-total">{cellar.line_total != null ? `£${Number(cellar.line_total).toFixed(2)}` : '— blocked —'}</span>
      </div>
    </div>
  );
}
